// ============ RÉGÉNÉRATION DU CONTENU PAR DÉFAUT D'UN THÈME ============
// « Régénérer le contenu par défaut » réapplique les gabarits du thème à
// TOUTES ses pages ET crée celles qui manquent, afin de garantir que le
// thème possède bien l'intégralité de ses pages (head, header, footer…).
//
// Ce module est volontairement sans état (stateless) : il peut être utilisé
// aussi bien pour un seul thème (éditeur) que pour tous les thèmes actifs
// (action groupée Super Admin).

import { supabase } from '@/lib/supabase';
import { inferBlueprintIdFromCategory } from '@/lib/themeBlueprints';
import { getBlueprintPageContent, blueprintNavTitle } from './blueprintPageContent';

export interface ThemePageDef {
  key: string;
  title: string;
}

/**
 * Liste de référence des pages d'un thème Zifek, dans l'ordre d'affichage.
 * Doit rester alignée avec PAGE_DEFS de l'éditeur de thème.
 */
export const THEME_PAGE_LIST: ThemePageDef[] = [
  { key: 'home', title: 'Page Home' },
  { key: 'head', title: 'Head' },
  { key: 'header', title: 'Header' },
  { key: 'navmenu', title: 'Nav Menu' },
  { key: 'footer', title: 'Footer' },
  { key: 'apropos', title: 'Page À propos' },
  { key: 'blog', title: 'Page Blog' },
  { key: 'detailsblog', title: 'Détail Blog' },
  { key: 'booking', title: 'Page Booking' },
  { key: 'bookingsuccess', title: 'Booking Success' },
  { key: 'conditionsdutilisation', title: 'Conditions' },
  { key: 'connexion', title: 'Page Connexion' },
  { key: 'login', title: 'Page Login' },
  { key: 'creationcompte', title: 'Création Compte' },
  { key: 'contact', title: 'Page Contact' },
  { key: 'detailsform', title: 'Détail Formulaire' },
  { key: 'detailsproduit', title: 'Détail Produit' },
  { key: 'detailsproduits', title: 'Détail Produits' },
  { key: 'detailsservice', title: 'Détail Service' },
  { key: 'equipe', title: 'Page Équipe' },
  { key: 'fichierai', title: 'Fichier AI' },
  { key: 'panier', title: 'Page Panier' },
  { key: 'politique', title: 'Politique' },
  { key: 'service', title: 'Page Services' },
  { key: 'produits', title: 'Boutique / Produits' },
  { key: 'hebergement', title: 'Page Hébergements' },
  { key: 'evenement', title: 'Page Événements' },
  { key: 'project', title: 'Page Projet' },
  { key: 'recuperation', title: 'Récupération' },
  { key: 'resetpassword', title: 'Reset Password' },
  { key: 'retours', title: 'Page Retours' },
  { key: 'workspacepublic', title: 'Workspace Public' },
  { key: 'marketplace', title: 'Marketplace' },
  { key: 'marketplace-seller', title: 'Page Vendeur' },
  { key: 'marketplace-customer', title: 'Page Client' },
  { key: 'faq', title: 'Page FAQ' },
  { key: 'gallery', title: 'Page Galerie' },
];

export interface RegenerateResult {
  themeId: number;
  updated: number;
  inserted: number;
}

/**
 * Régénère le contenu par défaut d'un thème :
 *  - met à jour chaque page existante avec le gabarit courant,
 *  - CRÉE les pages manquantes (garantit que toutes les pages existent),
 *  - synchronise le titre de navigation par défaut.
 */
export async function regenerateThemePages(
  themeId: number,
  blueprintId?: string | null,
): Promise<RegenerateResult> {
  const bpId = blueprintId || null;

  // Mémorise le type de commerce choisi (utile au badge + futur rendu).
  await supabase.from('sitewebtheme').update({ blueprint: bpId }).eq('id', themeId);

  const { data: existing } = await supabase
    .from('sitewebthemepage')
    .select('id, page_key')
    .eq('idtheme', themeId);

  const idByKey = new Map<string, number>();
  (existing || []).forEach((row: { id: number; page_key: string }) => {
    idByKey.set(row.page_key, row.id);
  });

  const now = new Date().toISOString();
  let updated = 0;
  let inserted = 0;
  const toInsert: Record<string, unknown>[] = [];

  for (const page of THEME_PAGE_LIST) {
    const content = getBlueprintPageContent(page.key, bpId);
    const existingId = idByKey.get(page.key);
    if (existingId != null) {
      await supabase
        .from('sitewebthemepage')
        .update({ content, title: page.title, updated_at: now })
        .eq('id', existingId);
      updated += 1;
    } else {
      toInsert.push({
        idtheme: themeId,
        page_key: page.key,
        title: page.title,
        content,
      });
    }
  }

  if (toInsert.length > 0) {
    await supabase.from('sitewebthemepage').insert(toInsert);
    inserted = toInsert.length;
  }

  // Titre de navigation par défaut (sitewebthemecontenu), s'il existe déjà.
  const { data: contenu } = await supabase
    .from('sitewebthemecontenu')
    .select('id')
    .eq('idtheme', themeId)
    .maybeSingle();
  if (contenu) {
    await supabase
      .from('sitewebthemecontenu')
      .update({ titrenavmenudefaut: blueprintNavTitle(bpId) })
      .eq('id', (contenu as { id: number }).id);
  }

  return { themeId, updated, inserted };
}

export interface BulkRegenerateTheme {
  id: number;
  titre?: string | null;
  blueprint?: string | null;
  typetheme?: string | null;
}

export interface BulkProgress {
  current: number;
  total: number;
  label: string;
}

export interface BulkRegenerateResult {
  themes: number;
  updated: number;
  inserted: number;
}

/**
 * Régénère le contenu par défaut de plusieurs thèmes (typiquement tous les
 * thèmes actifs) et crée leurs pages manquantes.
 *
 * @param themes        thèmes à régénérer
 * @param categoryNameFor fonction qui résout le nom de la catégorie d'un thème
 *                        (sert à deviner le type de commerce si absent)
 * @param onProgress    callback de progression
 */
export async function bulkRegenerateActiveThemes(
  themes: BulkRegenerateTheme[],
  categoryNameFor?: (typethemeId?: string | null) => string,
  onProgress?: (progress: BulkProgress) => void,
): Promise<BulkRegenerateResult> {
  let updated = 0;
  let inserted = 0;

  for (let i = 0; i < themes.length; i += 1) {
    const theme = themes[i];
    onProgress?.({ current: i + 1, total: themes.length, label: theme.titre || `Thème #${theme.id}` });

    const categoryName = categoryNameFor ? categoryNameFor(theme.typetheme) : null;
    const bpId = theme.blueprint || inferBlueprintIdFromCategory(categoryName) || null;
    const res = await regenerateThemePages(theme.id, bpId);
    updated += res.updated;
    inserted += res.inserted;
  }

  return { themes: themes.length, updated, inserted };
}